import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import {
  type ForwardedRef,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";

const tagVariants = cva(
  "inline-flex shrink-0 items-center justify-center text-nowrap [&_svg]:size-4",
  {
    variants: {
      variant: {
        primary: "bg-purple-500 text-white",
        "primary-black": "bg-black-950 text-white",

        secondary: "border border-purple-200 bg-purple-50 text-purple-700",
        tertiary: "border border-black-200 bg-white text-black-950",
        quaternary: "bg-black-100 text-black-700",

        // Accent
        success: "border border-green-200 bg-green-50 text-green-700",
        danger: "border border-red-200 bg-red-50 text-red-600",
        info: "border border-blue-200 bg-blue-50 text-blue-700",

        disabled: "bg-black-200 text-black-400",

        magenta: "border border-magenta-200 bg-magenta-50 text-magenta-700",
        orange: "border border-orange-200 bg-orange-50 text-orange-700",
        yellow: "border border-yellow-200 bg-yellow-50 text-yellow-700",
        gradient: "bg-blackPurple text-white",
      },
      size: {
        sm: "h-5 min-w-5 gap-0.5 rounded-md px-2 py-0.5 text-button-xxs",
        md: "h-6 min-w-6 gap-1 rounded-lg px-2.5 py-1 text-button-xs",
        lg: "h-7 min-w-7 gap-1.5 rounded-lg px-3 py-1.5 text-button-xs",
      },
      icon: {
        left: "[&_svg]:-ml-1.5",
        right: "[&_svg]:-mr-1.5",
        both: "",
      },
    },
    compoundVariants: [
      {
        size: "sm",
        icon: "both",
        class: "px-0.5",
      },
      {
        size: "md",
        icon: "both",
        class: "px-1",
      },
      {
        size: "lg",
        icon: "both",
        class: "px-1.5",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

type TagVariantType = VariantProps<typeof tagVariants>["variant"];

type TagProps = HTMLAttributes<HTMLSpanElement> &
  Omit<VariantProps<typeof tagVariants>, "icon"> & {
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    asChild?: boolean;
  };

function Tag(
  {
    className,
    variant,
    size,
    children,
    leftIcon = null,
    rightIcon = null,
    asChild = false,
    ...props
  }: TagProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const Comp = asChild ? Slot : "span";

  let icon: "left" | "right" | "both";
  if (leftIcon && !rightIcon) {
    icon = "left";
  } else if (rightIcon && !leftIcon) {
    icon = "right";
  } else if (rightIcon && leftIcon) {
    icon = "both";
  }

  return (
    <Comp
      className={tagVariants({ variant, size, icon, className })}
      ref={ref}
      {...props}
    >
      {leftIcon}
      <Slottable>{children}</Slottable>
      {rightIcon}
    </Comp>
  );
}
Tag.displayName = "Tag";

const TagWithRef = forwardRef(Tag);

export { TagWithRef as Tag, tagVariants, type TagVariantType };
